import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  generateOpportunityRoadmap,
  generateOpportunityRoadmapErrorMessage,
} from '../lib/generateOpportunityRoadmap';
import { saveBusinessOpportunity } from '../lib/saveBusinessOpportunity';
import { newId } from '../utils/id';
import { useAppData } from './AppDataContext';
import { useUser } from './UserContext';

export type RoadmapGenerationJob = {
  id: string;
  opportunityId: string;
  opportunityName: string;
  status: 'loading' | 'error';
  error: string | null;
};

type OpportunityRoadmapGenerationContextValue = {
  jobs: RoadmapGenerationJob[];
  isGenerating: (opportunityId: string) => boolean;
  getJobError: (opportunityId: string) => string | null;
  startRoadmapGeneration: (opportunityId: string) => Promise<void>;
  dismissError: (jobId: string) => void;
};

const OpportunityRoadmapGenerationContext = createContext<
  OpportunityRoadmapGenerationContextValue | undefined
>(undefined);

export function OpportunityRoadmapGenerationProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const { businessOpportunities } = useAppData();
  const opportunitiesRef = useRef(businessOpportunities);
  opportunitiesRef.current = businessOpportunities;

  const [jobs, setJobs] = useState<RoadmapGenerationJob[]>([]);
  const runningIdsRef = useRef(new Set<string>());

  const isGenerating = useCallback(
    (opportunityId: string) =>
      jobs.some((job) => job.opportunityId === opportunityId && job.status === 'loading'),
    [jobs]
  );

  const getJobError = useCallback(
    (opportunityId: string) => {
      const job = jobs.find((entry) => entry.opportunityId === opportunityId && entry.status === 'error');
      return job?.error ?? null;
    },
    [jobs]
  );

  const dismissError = useCallback((jobId: string) => {
    setJobs((current) => current.filter((job) => job.id !== jobId));
  }, []);

  const startRoadmapGeneration = useCallback(
    async (opportunityId: string) => {
      const trimmedId = opportunityId.trim();
      if (!trimmedId) return;
      if (!userId) return;
      if (runningIdsRef.current.has(trimmedId)) return;

      const opportunity = opportunitiesRef.current?.find((entry) => entry.id === trimmedId);
      const opportunityName = opportunity?.name?.trim() || 'Opportunity';

      runningIdsRef.current.add(trimmedId);
      const jobId = newId();
      setJobs((current) => [
        ...current.filter(
          (job) => !(job.opportunityId === trimmedId && job.status === 'error')
        ),
        {
          id: jobId,
          opportunityId: trimmedId,
          opportunityName,
          status: 'loading',
          error: null,
        },
      ]);

      try {
        const roadmap = await generateOpportunityRoadmap(trimmedId);
        const current = opportunitiesRef.current?.find((entry) => entry.id === trimmedId);
        if (current) {
          await saveBusinessOpportunity(userId, {
            id: current.id,
            name: current.name,
            industry: current.industry,
            ideaSummary: current.ideaSummary,
            status: current.status,
            ideaIntelligence: current.ideaIntelligence,
            refinements: current.refinements,
            feedback: current.feedback,
            roadmap,
            promotedCaseId: current.promotedCaseId,
            existing: current,
          });
        }
        setJobs((currentJobs) => currentJobs.filter((job) => job.id !== jobId));
      } catch (err) {
        const message = generateOpportunityRoadmapErrorMessage(err);
        setJobs((currentJobs) =>
          currentJobs.map((job) =>
            job.id === jobId ? { ...job, status: 'error', error: message } : job
          )
        );
      } finally {
        runningIdsRef.current.delete(trimmedId);
      }
    },
    [userId]
  );

  const value = useMemo(
    () => ({
      jobs,
      isGenerating,
      getJobError,
      startRoadmapGeneration,
      dismissError,
    }),
    [jobs, isGenerating, getJobError, startRoadmapGeneration, dismissError]
  );

  return (
    <OpportunityRoadmapGenerationContext.Provider value={value}>
      {children}
    </OpportunityRoadmapGenerationContext.Provider>
  );
}

export function useOpportunityRoadmapGeneration(): OpportunityRoadmapGenerationContextValue {
  const ctx = useContext(OpportunityRoadmapGenerationContext);
  if (!ctx) {
    throw new Error(
      'useOpportunityRoadmapGeneration must be used within OpportunityRoadmapGenerationProvider'
    );
  }
  return ctx;
}
